import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useUser } from '../../../shared/context/UserContext';
import { useProject } from '../../../shared/context/ProjectContext';
import { getSites } from '../../inventory/api/inventoryApi';
import { getLifecycleTools, searchLifecycleTools } from '../api/lifecycleApi';
import { matchesCanonicalSearch } from '../../../shared/searchFilter';
import { MIN_QUERY_LENGTH } from '../../../shared/searchFields';
import LifecycleToolList, { eowBadgeKey } from '../components/LifecycleToolList';
import LifecycleDetailPanel from '../components/LifecycleDetailPanel';
import { mergeSearchRowsWithLoaded } from '../utils/lifecycleSearchRows';
import GridView from '../../../components/GridView/GridView';
import GridModeToggle from '../../../components/GridView/GridModeToggle';
import { useGridState, sortRecords } from '../../../components/GridView/useGridState';
import { useLeftPanelCollapse } from '../../../shared/hooks/useLeftPanelCollapse';
import LeftPanelCollapseToggle from '../../../shared/components/LeftPanelCollapseToggle';
import { useScrollToTopOnMount } from '../../../shared/hooks/useScrollToTopOnMount';
import { apiFetch } from '../../../auth/apiFetch';
import './lifecycleRecon.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:7071/api';

/* ── Splitter persistence ─────────────────────────────────────────────── */
const SPLIT_MIN = 0.25;
const SPLIT_MAX = 0.50;
const SPLIT_DEFAULT = 0.35;

function getSplitKey(email) {
  return `tmg_lifecycle_split_ratio_${email || 'default'}`;
}

function loadSplitRatio(email) {
  try {
    const v = parseFloat(localStorage.getItem(getSplitKey(email)));
    if (!isNaN(v) && v >= SPLIT_MIN && v <= SPLIT_MAX) return v;
  } catch { /* ignore */ }
  return SPLIT_DEFAULT;
}

function saveSplitRatio(email, ratio) {
  try { localStorage.setItem(getSplitKey(email), String(ratio)); } catch { /* ignore */ }
}

function getUniqueValues(tools, key) {
  const set = new Set();
  for (const t of tools) {
    const v = t[key];
    if (v != null && v !== '') set.add(v);
  }
  return [...set].sort();
}

export default function LifecycleReconPage() {
  const { callerEmail } = useUser();
  const { resolveProject, showProjectPicker, availableProjects, selectProject } = useProject();

  /* ── Grid mode state (filteredTools wired in after it's computed) ──── */
  const gridState = useGridState(callerEmail, 'lifecycle');
  const {
    activeMode, setActiveMode,
    columnVisibility, toggleColumn, showAllColumns, hideAllColumns,
    pendingChanges, setPendingChange, clearPendingChanges, isDirty,
    sortKeys, setSortKeys,
    savedViews, activeViewId, activeViewName, saveView, deleteView, loadView,
  } = gridState;

  /* ── Left-panel collapse (per-user, per-screen) ────────────────────── */
  const { collapsed, toggle } = useLeftPanelCollapse(callerEmail, 'lifecycle');

  /* ── Site selector state ───────────────────────────────────────────── */
  const [sites, setSites]         = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  const [sitesLoading, setSitesLoading] = useState(true);

  /* ── Tool list state ───────────────────────────────────────────────── */
  const [tools, setTools]         = useState([]);
  const [toolsLoading, setToolsLoading] = useState(false);
  const [search, setSearch]       = useState('');
  const [selectedToolId, setSelectedToolId] = useState(null);
  // Server-side canonical search (search_inventory) when query >= min length;
  // null = fall back to the full loaded `tools` list.
  const [searchResults, setSearchResults] = useState(null);
  const searchDebounceRef = useRef(null);

  /* ── Filter state — four simple dropdowns ──────────────────────────── */
  const [filterList, setFilterList]             = useState('');
  const [filterReconStatus, setFilterReconStatus] = useState('');
  const [filterSiteLocation, setFilterSiteLocation] = useState('');
  const [filterDetailLocation, setFilterDetailLocation] = useState('');
  const [filterEow, setFilterEow] = useState('');
  const [filterWorkflow, setFilterWorkflow] = useState('');

  /* ── Resizable splitter state ──────────────────────────────────────── */
  const [splitRatio, setSplitRatio] = useState(() => loadSplitRatio(callerEmail));
  const draggingRef = useRef(false);
  const pageRef = useRef(null);
  const rightRef = useRef(null);

  // Reset scroll on entry (window = load-bearing; right pane = inner container)
  useScrollToTopOnMount(rightRef);

  const handleSplitterMouseDown = useCallback((e) => {
    e.preventDefault();
    draggingRef.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    function onMouseMove(ev) {
      if (!draggingRef.current || !pageRef.current) return;
      const rect = pageRef.current.getBoundingClientRect();
      const ratio = (ev.clientX - rect.left) / rect.width;
      const clamped = Math.min(SPLIT_MAX, Math.max(SPLIT_MIN, ratio));
      setSplitRatio(clamped);
    }

    function onMouseUp() {
      if (draggingRef.current) {
        draggingRef.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        setSplitRatio(prev => { saveSplitRatio(callerEmail, prev); return prev; });
      }
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    }

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [callerEmail]);

  /* ── Dynamic filter options from loaded tools ──────────────────────── */
  const listNumbers = useMemo(() => getUniqueValues(tools, 'list_number'), [tools]);
  const siteLocations = useMemo(() => getUniqueValues(tools, 'site_location'), [tools]);
  const detailLocations = useMemo(() => getUniqueValues(tools, 'detail_location'), [tools]);

  /* ── Reset filters when tools change ───────────────────────────────── */
  useEffect(() => {
    setFilterList('');
    setFilterReconStatus('');
    setFilterSiteLocation('');
    setFilterDetailLocation('');
    setFilterEow('');
    setFilterWorkflow('');
  }, [tools]);

  const hasActiveFilters = filterList || filterReconStatus || filterSiteLocation || filterDetailLocation || filterEow || filterWorkflow;

  function clearAllFilters() {
    setFilterList('');
    setFilterReconStatus('');
    setFilterSiteLocation('');
    setFilterDetailLocation('');
    setFilterEow('');
    setFilterWorkflow('');
  }

  /* ── Load sites on mount ───────────────────────────────────────────── */
  useEffect(() => {
    if (!callerEmail) return;
    setSitesLoading(true);
    getSites(callerEmail, 'LIFECYCLE')
      .then(list => setSites(list || []))
      .catch(() => setSites([]))
      .finally(() => setSitesLoading(false));
  }, [callerEmail]);

  /* ── Auto-select when only one site is available ─────────────────────── */
  useEffect(() => {
    if (sites.length === 1 && !selectedSite) {
      setSelectedSite(sites[0].site_id);
    }
  }, [sites, selectedSite]);

  /* ── Load tools when site changes ──────────────────────────────────── */
  const loadTools = useCallback((keepSelection = false) => {
    if (!selectedSite || !callerEmail) { setTools([]); return; }
    setToolsLoading(true);
    if (!keepSelection) setSelectedToolId(null);

    getLifecycleTools(selectedSite, callerEmail)
      .then(list => {
        setTools(list || []);
        if (!keepSelection && list && list.length > 0) {
          setSelectedToolId(list[0].id || list[0].tool_id);
        }
      })
      .catch(() => setTools([]))
      .finally(() => setToolsLoading(false));
  }, [selectedSite, callerEmail]);

  useEffect(() => { loadTools(false); }, [loadTools]);

  /* ── Server-side canonical search (>= MIN_QUERY_LENGTH) ──────────────────
     Mirrors the S4/S5 reference: debounce 300ms → search_inventory; shorter
     queries clear results so the full loaded list (shared client filter)
     shows instead. */
  useEffect(() => {
    const q = (search || '').trim();
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if (!selectedSite || !callerEmail || q.length < MIN_QUERY_LENGTH) {
      setSearchResults(null);
      return;
    }
    searchDebounceRef.current = setTimeout(() => {
      searchLifecycleTools(selectedSite, q, callerEmail)
        .then(data => {
          const list = Array.isArray(data) ? data : [];
          // search_inventory returns Tools documents and never reads
          // LifecycleProfile. Rebuild each row from the already-loaded
          // action=list row, which carries the profile's fields, so the
          // badge, list number, recon status and grid columns match the
          // unfiltered list (B-284).
          setSearchResults(mergeSearchRowsWithLoaded(list, tools));
        })
        .catch(() => setSearchResults([]));
    }, 300);
    return () => { if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current); };
  }, [search, selectedSite, callerEmail, tools]);

  /* ── Resolve project when site changes ───────────────────────────────── */
  useEffect(() => { resolveProject(selectedSite); }, [selectedSite, resolveProject]);

  /* ── Filtered tools (search + filters, AND logic) ──────────────────── */
  const filteredTools = useMemo(() => {
    // searchResults (server canonical search, query >= MIN) is authoritative
    // for which rows show; each row is its loaded row (B-284). Dropdown
    // filters below still apply. null = short/no query
    // → filter the loaded list with the shared canonical gate.
    const usingServer = searchResults !== null;
    let result = usingServer ? searchResults : tools;

    if (!usingServer) {
      result = result.filter(t => matchesCanonicalSearch(t, search));
    }

    // List number filter
    if (filterList) {
      result = result.filter(t => (t.list_number || '') === filterList);
    }

    // Recon status filter
    if (filterReconStatus) {
      result = result.filter(t => (t.recon_status || '') === filterReconStatus);
    }

    // Site location filter
    if (filterSiteLocation) {
      result = result.filter(t => (t.site_location || '') === filterSiteLocation);
    }

    // Detail location filter
    if (filterDetailLocation) {
      result = result.filter(t => (t.detail_location || '') === filterDetailLocation);
    }

    // EOW disposition filter: by the key the row's badge shows (eowBadgeKey), so a tool falls
    // under exactly one option. SHOTS_PENDING is the option for SHOTS_INCOMPLETE (B-272).
    if (filterEow) {
      const want = filterEow === 'SHOTS_PENDING' ? 'SHOTS_INCOMPLETE' : filterEow;
      result = result.filter(t => eowBadgeKey(t) === want);
    }

    // Workflow status filter
    if (filterWorkflow) {
      result = result.filter(t => (t.workflow_status || '') === filterWorkflow);
    }

    return result;
  }, [tools, searchResults, search, filterList, filterReconStatus, filterSiteLocation, filterDetailLocation, filterEow, filterWorkflow]);

  /* ── Sorted records for grid view (raw filteredTools preserved for dirty comparison) */
  const sortedTools = useMemo(
    () => sortRecords(filteredTools, sortKeys),
    [filteredTools, sortKeys]
  );

  /* ── Completeness count across full (unfiltered) tool set ──────────── */
  const completeCount = useMemo(
    () => tools.filter(t => t.completeness?.is_complete).length,
    [tools],
  );

  /* ── Selected tool object ──────────────────────────────────────────── */
  const selectedTool = tools.find(t => (t.id || t.tool_id) === selectedToolId) || null;

  /* ── After save, refresh the tool list but keep selection ────────────── */
  const handleToolSaved = useCallback(() => {
    loadTools(true);
  }, [loadTools]);

  /* ── Grid batch save ───────────────────────────────────────────────── */
  const [gridSaving, setGridSaving] = useState(false);
  const [gridSaveError, setGridSaveError] = useState(null);

  const handleGridSave = useCallback(async (pendingChangesMap) => {
    const TOOL_FIELDS = new Set(['list_number']);

    const changes = [];
    for (const [tool_id, fieldMap] of pendingChangesMap) {
      const lifecycle_changes = {};
      const tool_changes = {};
      for (const [field, value] of Object.entries(fieldMap)) {
        if (TOOL_FIELDS.has(field)) tool_changes[field] = value;
        else lifecycle_changes[field] = value;
      }
      changes.push({ tool_id, lifecycle_changes, tool_changes });
    }

    setGridSaving(true);
    setGridSaveError(null);

    try {
      const response = await apiFetch(`${API_BASE_URL}/batch_save_grid`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          screen: 'lifecycle',
          caller_email: callerEmail,
          changes,
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        // Do NOT clear dirty state on failure
        setGridSaveError(err.message || `Save failed (${response.status})`);
        return;
      }

      // Success — clear dirty state and refresh the tool list
      clearPendingChanges();
      setGridSaveError(null);
      loadTools(true);
    } catch (err) {
      setGridSaveError(err.message || 'Network error');
    } finally {
      setGridSaving(false);
    }
  }, [callerEmail, clearPendingChanges, loadTools]);

  const leftPct = `${(splitRatio * 100).toFixed(1)}%`;

  return (
    <div className="lrc-page" ref={pageRef}>
      {/* ── Left pane ──────────────────────────────────────────────────── */}
      <div
        className={`lrc-left${collapsed ? ' lrc-left-collapsed' : ''}`}
        style={collapsed ? undefined : { width: leftPct, minWidth: '280px', maxWidth: 'none' }}
      >
        <div className="lrc-left-header">
          <h2 className="lrc-left-title">Lifecycle Recon</h2>
          <select
            className="lrc-site-select"
            value={selectedSite}
            onChange={e => setSelectedSite(e.target.value)}
            disabled={sitesLoading}
          >
            <option value="">
              {sitesLoading ? 'Loading sites...' : '— Select a site —'}
            </option>
            {sites.map(s => (
              <option key={s.site_id} value={s.site_id}>
                {s.site_city
                  ? `${s.site_name} (${s.site_city})`
                  : s.supplier_site_code
                    ? `${s.site_name} (${s.supplier_site_code})`
                    : s.site_name}
              </option>
            ))}
          </select>
        </div>

        {selectedSite && (
          <>
            {/* ── Search bar ───────────────────────────────────────────── */}
            <div className="lrc-search-wrap">
              <div className="lrc-search-box">
                <input
                  type="text"
                  className="lrc-search-input"
                  placeholder="Search by ID, part number, tag, location..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
                {search && (
                  <button
                    className="lrc-search-clear"
                    onClick={() => setSearch('')}
                    type="button"
                    aria-label="Clear search"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>

            {/* ── Filter dropdowns ─────────────────────────────────────── */}
            <div className="lrc-filter-bar">
              <select className="lrc-filter-select" value={filterList}
                onChange={e => setFilterList(e.target.value)}>
                <option value="">All Lists</option>
                {listNumbers.map(ln => <option key={ln} value={ln}>{ln}</option>)}
              </select>

              <select className="lrc-filter-select" value={filterReconStatus}
                onChange={e => setFilterReconStatus(e.target.value)}>
                <option value="">All Status</option>
                <option value="OPEN">Open</option>
                <option value="CLOSED">Closed</option>
              </select>

              <select className="lrc-filter-select" value={filterSiteLocation}
                onChange={e => setFilterSiteLocation(e.target.value)}>
                <option value="">All Locations</option>
                {siteLocations.map(loc => <option key={loc} value={loc}>{loc}</option>)}
              </select>

              <select className="lrc-filter-select" value={filterDetailLocation}
                onChange={e => setFilterDetailLocation(e.target.value)}>
                <option value="">All Detail</option>
                {detailLocations.map(dl => <option key={dl} value={dl}>{dl}</option>)}
              </select>

              <select className="lrc-filter-select" value={filterEow}
                onChange={e => setFilterEow(e.target.value)}>
                <option value="">All EOW</option>
                <option value="URGENT">Urgent</option>
                <option value="NEAR_TERM">Near Term</option>
                <option value="MONITORING">Monitoring</option>
                <option value="CONDITION_UNASSESSED">Condition Needed</option>
                <option value="DEMAND_MISSING">Forecast Pending</option>
                <option value="SHOTS_PENDING">Pieces Pending</option>
              </select>

              <select className="lrc-filter-select" value={filterWorkflow}
                onChange={e => setFilterWorkflow(e.target.value)}>
                <option value="">All Workflow</option>
                <option value="ACTIVE">Active</option>
                <option value="RETIRED">Retired</option>
                <option value="SCRAPPED">Scrapped</option>
                <option value="PENDING_CONFIRMATION">Pending Confirmation</option>
              </select>

              {hasActiveFilters && (
                <button className="lrc-filter-clear" onClick={clearAllFilters} type="button">
                  Clear
                </button>
              )}
            </div>

            {/* ── Record count + completeness summary ────────────────── */}
            <div className="lrc-summary-line">
              Showing {filteredTools.length} of {tools.length} tools &middot; {completeCount} complete
            </div>
          </>
        )}

        <div className="lrc-tool-list">
          {!selectedSite && !toolsLoading && (
            <div className="lrc-loading">Select a site above.</div>
          )}
          {selectedSite && (
            <LifecycleToolList
              tools={filteredTools}
              selectedToolId={selectedToolId}
              onSelectTool={setSelectedToolId}
              loading={toolsLoading}
              searchQuery={search}
            />
          )}
        </div>
      </div>

      {/* ── Resizable splitter handle ──────────────────────────────────── */}
      {!collapsed && (
        <div
          className="lrc-splitter"
          onMouseDown={handleSplitterMouseDown}
        />
      )}

      {/* ── Right pane ─────────────────────────────────────────────────── */}
      <div className="lrc-right" ref={rightRef}>
        {/* Gate is (selectedSite || collapsed), not selectedSite alone.
            The site picker lives inside .lrc-left and is display:none'd by
            .lrc-left-collapsed (lifecycleRecon.css:314). collapsed persists to
            localStorage; selectedSite does not. Gating this toggle on
            selectedSite alone made the only un-collapse control unreachable
            once a multi-site user collapsed the pane — permanently, across
            sessions. Do not narrow this gate back. */}
        {(selectedSite || collapsed) && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'var(--space-4) var(--space-6)', borderBottom: '1px solid var(--color-border)' }}>
            {/* Expand work area = collapse the left rail. */}
            <LeftPanelCollapseToggle collapsed={collapsed} onToggle={toggle} />
            {selectedSite && (
              <GridModeToggle activeMode={activeMode} onChange={setActiveMode} />
            )}
          </div>
        )}
        {activeMode === 'grid' ? (
          <GridView
            records={filteredTools}
            sortedRecords={sortedTools}
            sortKeys={sortKeys}
            setSortKeys={setSortKeys}
            screen="lifecycle"
            callerEmail={callerEmail}
            siteId={selectedSite}
            onSave={handleGridSave}
            onCancel={() => clearPendingChanges()}
            columnVisibility={columnVisibility}
            toggleColumn={toggleColumn}
            showAllColumns={showAllColumns}
            hideAllColumns={hideAllColumns}
            pendingChanges={pendingChanges}
            setPendingChange={setPendingChange}
            clearPendingChanges={clearPendingChanges}
            isDirty={isDirty}
            saving={gridSaving}
            saveError={gridSaveError}
            savedViews={savedViews}
            activeViewId={activeViewId}
            activeViewName={activeViewName}
            saveView={saveView}
            deleteView={deleteView}
            loadView={loadView}
          />
        ) : (
          <>
            {!selectedToolId && (
              <div className="lrc-right-empty">Select a tool to view its lifecycle profile</div>
            )}
            {selectedTool && (
              <LifecycleDetailPanel
                tool={selectedTool}
                onSaved={handleToolSaved}
              />
            )}
          </>
        )}
      </div>

      {/* ── Project picker (shown when multiple projects for a site) ──── */}
      {showProjectPicker && (
        <div className="lrc-project-picker-overlay">
          <div className="lrc-project-picker">
            <h3>Which project?</h3>
            <p>Multiple projects exist for this site. Select one to continue.</p>
            {availableProjects.map(p => (
              <button
                key={p.id}
                className="lrc-project-picker-btn"
                onClick={() => selectProject(p)}
              >
                {p.project_name}
                <span className="lrc-project-picker-meta">
                  {p.workflow_type} &middot; {p.status}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
